import { useState } from 'react';

import { Modal, useToast } from '../ui/Overlay';
import { Button } from '../ui/Button';
import { Field, Input, Select, SearchInput } from '../ui/Field';
import { Icon } from '../ui/Icon';
import { Badge } from '../ui/Badge';
import { useMutation, useResource } from '../../hooks/useResource';
import { api } from '../../lib/api';
import * as fmt from '../../lib/format';

/**
 * Provision a device into platform inventory.
 *
 * The shared secret is generated by the server and shown exactly once. That is
 * deliberate: a secret the platform can display on demand is a secret the
 * platform is storing in a form it could leak, and this one is stored as a
 * hash. If it is lost, it is rotated — never recovered.
 */
export function ProvisionDeviceModal({ open, onClose, organizations, onCreated }) {
  const toast = useToast();
  const empty = { imei: '', serial: '', orgId: '', hardwareModel: '', firmwareVersion: '', notes: '' };
  const [form, setForm] = useState(empty);
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation(async () => {
    const result = await api.post('/api/devices', {
      imei: form.imei.trim(),
      serial: form.serial || undefined,
      orgId: form.orgId || undefined,
      hardwareModel: form.hardwareModel || undefined,
      firmwareVersion: form.firmwareVersion || undefined,
      notes: form.notes || undefined,
    });
    return result;
  });

  const imeiValid = /^\d{15}$/.test(form.imei.trim());

  const submit = async () => {
    try {
      const result = await create.run();
      setSecret(result.secret);
      setForm(empty);
      onCreated?.();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const finish = () => {
    setSecret(null);
    setCopied(false);
    onClose();
  };

  // Once the secret is on screen the dialog has one job: get it written down.
  if (secret) {
    return (
      <Modal
        open={open}
        onClose={finish}
        title="Device provisioned"
        description="Write this secret into the device now. It cannot be shown again."
        footer={<Button variant="primary" onClick={finish}>Done</Button>}
      >
        <SecretPanel secret={secret} copied={copied} onCopied={setCopied} />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Provision a device"
      description="Adds a TCU to platform inventory and issues its credentials."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.pending} onClick={submit} disabled={!imeiValid}>
            Provision
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="IMEI"
          required
          hint="Fifteen digits, printed on the device."
          error={form.imei && !imeiValid ? 'An IMEI is exactly 15 digits.' : null}
        >
          <Input
            value={form.imei}
            onChange={set('imei')}
            placeholder="869000000000123"
            inputMode="numeric"
            autoFocus
            invalid={Boolean(form.imei) && !imeiValid}
          />
        </Field>

        <Field label="Serial number">
          <Input value={form.serial} onChange={set('serial')} placeholder="SN-000123" />
        </Field>

        <Field
          label="Organisation"
          hint="Leave empty to keep it in the unallocated pool and assign it later."
        >
          <Select
            value={form.orgId}
            onChange={set('orgId')}
            placeholder="Unallocated"
            options={(organizations || []).map((org) => ({ value: org.id, label: org.name }))}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Hardware model" hint="The device corrects this itself on first contact.">
            <Input value={form.hardwareModel} onChange={set('hardwareModel')} placeholder="TCU-4G-V2" />
          </Field>
          <Field label="Firmware">
            <Input value={form.firmwareVersion} onChange={set('firmwareVersion')} placeholder="1.4.2" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Fit a device to a vehicle, or take it off.
 *
 * Assignment history is what makes telemetry survive a device swap, so this is
 * a real operation with a real record — not an edit to a field.
 */
export function AssignDeviceModal({ open, onClose, device, onAssigned }) {
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [vehicleId, setVehicleId] = useState('');

  const vehicles = useResource(open ? '/api/vehicles' : null, { limit: 500 });

  const assign = useMutation(async (targetVehicleId) => {
    await api.post(`/api/devices/${device.id}/assign`, { vehicleId: targetVehicleId || null });
  });

  const candidates = (vehicles.data?.vehicles || []).filter((vehicle) => {
    if (vehicle.device_id && vehicle.device_id !== device?.id) return false; // already fitted
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [vehicle.reg_number, vehicle.fleet_name, vehicle.model]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(needle));
  });

  const run = async (targetVehicleId) => {
    try {
      await assign.run(targetVehicleId);
      toast.success(targetVehicleId ? 'Device fitted.' : 'Device removed from the vehicle.');
      setVehicleId('');
      setQuery('');
      onAssigned();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!device) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={device.vehicle_id ? 'Move this device' : 'Fit this device to a vehicle'}
      description={
        device.vehicle_id
          ? `Currently fitted to ${device.reg_number}. Telemetry already recorded stays with that vehicle.`
          : 'Telemetry from this device will be recorded against the vehicle you choose.'
      }
      footer={
        <>
          {device.vehicle_id && (
            <Button
              variant="danger"
              className="mr-auto"
              loading={assign.pending}
              onClick={() => run(null)}
            >
              Unassign
            </Button>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={assign.pending}
            disabled={!vehicleId}
            onClick={() => run(vehicleId)}
          >
            Fit device
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SearchInput value={query} onChange={setQuery} placeholder="Search vehicles" />

        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {candidates.length === 0 && (
            <li className="px-2 py-6 text-center text-[13px] text-ink-3">
              Every vehicle in your scope already has a device fitted.
            </li>
          )}
          {candidates.map((vehicle) => (
            <li key={vehicle.id}>
              <label
                className={[
                  'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors',
                  vehicleId === vehicle.id
                    ? 'border-brand-border bg-brand-soft'
                    : 'border-line hover:bg-surface-2',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="vehicle"
                  className="h-4 w-4 accent-[color:var(--brand)]"
                  checked={vehicleId === vehicle.id}
                  onChange={() => setVehicleId(vehicle.id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-medium text-ink">
                    {vehicle.reg_number}
                  </span>
                  <span className="block truncate text-[11.5px] text-ink-3">
                    {vehicle.fleet_name || 'No fleet'}
                    {vehicle.model ? ` · ${vehicle.model}` : ''}
                  </span>
                </span>
                {vehicle.device_id === device.id && (
                  <Badge tone="good" size="sm" icon={null}>
                    current
                  </Badge>
                )}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

/**
 * A shared secret, shown exactly once.
 *
 * Provisioning and rotation both end here, and they must end here identically —
 * two versions of "this is the only time you will see it" is how one of them
 * quietly loses the warning.
 */
function SecretPanel({ secret, copied, onCopied }) {
  const toast = useToast();

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning-ink">
        <Icon name="warning" size="sm" className="mt-0.5" />
        <p>
          This is the only time the secret is displayed. The platform stores a hash of it, not
          the value. If it is lost, rotate it again — it can never be recovered.
        </p>
      </div>

      <code className="block break-all rounded-lg border border-line bg-surface-2 p-3 font-mono text-[13px] text-ink">
        {secret}
      </code>

      <Button
        variant="secondary"
        size="sm"
        icon={copied ? 'check' : 'download'}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(secret);
            onCopied(true);
          } catch {
            toast.error('Your browser blocked the clipboard. Select the text and copy it.');
          }
        }}
      >
        {copied ? 'Copied' : 'Copy secret'}
      </Button>
    </div>
  );
}

/**
 * Rotate a device's shared secret.
 *
 * This is a destructive operation dressed as a small one: the moment it runs,
 * the device in the field is using a secret the platform no longer accepts and
 * stops reporting until an engineer writes the new one in. So it asks first,
 * and says plainly what will happen.
 */
export function RotateSecretModal({ open, onClose, device }) {
  const toast = useToast();
  const [secret, setSecret] = useState(null);
  const [copied, setCopied] = useState(false);

  const rotate = useMutation(async () => api.post(`/api/devices/${device.id}/rotate-secret`, {}));

  const finish = () => {
    setSecret(null);
    setCopied(false);
    onClose();
  };

  const run = async () => {
    try {
      const result = await rotate.run();
      setSecret(result.secret);
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!device) return null;

  if (secret) {
    return (
      <Modal
        open={open}
        onClose={finish}
        title="New secret issued"
        description={`${device.imei} will not connect again until this is written to it.`}
        footer={<Button variant="primary" onClick={finish}>Done</Button>}
      >
        <SecretPanel secret={secret} copied={copied} onCopied={setCopied} />
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Rotate device secret"
      description={`Issues new credentials for ${device.imei}.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={rotate.pending} onClick={run}>
            Rotate secret
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-2 rounded-lg border border-critical/30 bg-critical-soft px-3 py-2.5 text-[13px] text-critical-ink">
        <Icon name="warning" size="sm" className="mt-0.5" />
        <p>
          This device stops reporting the moment you do this, and stays silent until an engineer
          writes the new secret into it. Telemetry already recorded is unaffected. Rotate a secret
          when it has been exposed, when the device changes hands, or after it is recovered from
          somewhere it should not have been.
        </p>
      </div>
    </Modal>
  );
}

/**
 * Move a device between organisations, or back to the unallocated pool.
 *
 * A device belongs to a tenant, and everything it reports belongs to that
 * tenant with it — which is why the server refuses to move one that is still
 * fitted to a vehicle rather than silently dragging a vehicle's history across
 * a tenancy boundary.
 */
export function AllocateDeviceModal({ open, onClose, device, organizations, onAllocated }) {
  const toast = useToast();
  const [orgId, setOrgId] = useState('');

  const allocate = useMutation(async () =>
    api.post(`/api/devices/${device.id}/allocate`, { orgId: orgId || null })
  );

  const run = async () => {
    try {
      await allocate.run();
      toast.success(orgId ? 'Device allocated.' : 'Device returned to the unallocated pool.');
      onAllocated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!device) return null;

  const current = (organizations || []).find((org) => org.id === device.org_id);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Allocate device"
      description={
        current
          ? `Currently with ${current.name}.`
          : 'Currently unallocated — no organisation can see it.'
      }
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={allocate.pending}
            onClick={run}
            disabled={orgId === (device.org_id || '')}
          >
            {orgId ? 'Allocate' : 'Return to pool'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Organisation"
          hint="Choose nothing to return it to the unallocated pool."
        >
          <Select
            value={orgId}
            onChange={(event) => setOrgId(event.target.value)}
            placeholder="Unallocated"
            options={(organizations || []).map((org) => ({ value: org.id, label: org.name }))}
          />
        </Field>

        {device.vehicle_id && (
          <p className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-[12.5px] text-warning-ink">
            This device is fitted to {device.reg_number}. Take it off the vehicle before moving it
            to another organisation.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * The raw packets, as received.
 *
 * When someone says "the platform is wrong", this is the screen that settles
 * it: the bytes on the wire, whether they decoded, and the decoder's own error
 * if they did not. It is deliberately restricted to `telemetry:raw` — a payload
 * carries credentials-adjacent identifiers and is not operational data.
 */
export function RawPacketsModal({ open, onClose, device }) {
  const packets = useResource(open && device ? `/api/devices/${device.id}/packets` : null, {
    limit: 50,
  });

  if (!device) return null;

  const rows = packets.data?.packets || [];
  const failures = rows.filter((packet) => !packet.decode_ok).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Raw packets"
      description={
        packets.loading
          ? 'Loading…'
          : `Last ${rows.length} received from ${device.imei}${failures ? ` · ${failures} failed to decode` : ''}`
      }
      width="lg"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {rows.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-[13px] text-ink-2">
            {packets.loading ? 'Loading…' : 'No raw frames kept for this device.'}
          </p>
          {!packets.loading && (
            // Not the same as "it has sent nothing". By default only packets
            // that failed to decode or arrived from an unknown device are
            // stored — everything that worked becomes telemetry and the bytes
            // are discarded. Saying so is the difference between "the device
            // is silent" and "we chose not to keep this".
            <p className="mx-auto mt-2 max-w-md text-[12.5px] text-ink-3">
              Frames are kept only when they fail to decode. To capture everything this
              device sends, set <code className="font-mono">STORE_RAW_PACKETS=all</code> on the
              ingest service — then turn it off again, because a payload carries the device&rsquo;s
              own identifiers.
            </p>
          )}
        </div>
      ) : (
        <ul className="max-h-[26rem] divide-y divide-line overflow-y-auto rounded-lg border border-line">
          {rows.map((packet) => (
            <li key={`${packet.time}-${packet.bytes}`} className="px-3 py-2">
              <div className="flex items-center gap-2">
                <Badge tone={packet.decode_ok ? 'good' : 'critical'} size="sm" icon={null}>
                  {packet.decode_ok ? 'decoded' : 'failed'}
                </Badge>
                <span className="text-[12px] text-ink-2">{fmt.dateTime(packet.time)}</span>
                <span className="text-[11.5px] text-ink-3">
                  {packet.transport} · v{packet.schema_version} · {packet.bytes} bytes
                </span>
              </div>
              {packet.decode_error && (
                <p className="mt-1 text-[12px] text-critical-ink">{packet.decode_error}</p>
              )}
              <code className="mt-1 block truncate font-mono text-[11.5px] text-ink-3" title={packet.payload_hex}>
                {packet.payload_hex}
              </code>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * Reset a device.
 *
 * A reboot, not a wipe. That distinction is the whole design of this modal:
 * the word "reset" means different things on different hardware, and an
 * operator who presses it expecting a reboot and gets a credential wipe has
 * just lost a vehicle until someone drives out to it. So the modal says
 * exactly what happens, what survives, and how long it takes — and the
 * confirmation is a real step rather than a reflex.
 */
export function ResetDeviceModal({ open, onClose, device, onQueued }) {
  const toast = useToast();
  const reset = useMutation(async () =>
    api.post(`/api/devices/${device.id}/commands`, { command: 'reboot', confirm: true })
  );

  const run = async () => {
    try {
      const result = await reset.run();
      toast.success('Reset queued. The device picks it up on its next connection.');
      onQueued?.(result.command);
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!device) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reset device"
      description={`Restarts ${device.imei}.`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={reset.pending} onClick={run}>
            Reset device
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-[13px]">
        <div className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2.5 text-warning-ink">
          <Icon name="warning" size="sm" className="mt-0.5" />
          <p>
            The device stops reporting for roughly a minute while it restarts. Anything it
            captures during that window is lost — on a vehicle in motion that is a gap in the
            track, not a gap in the record you can fill in later.
          </p>
        </div>

        <ul className="space-y-1.5 text-ink-2">
          <li className="flex items-start gap-2">
            <Icon name="check" size="xs" className="mt-1 text-good" />
            <span>Packets already buffered on the device survive and are sent when it returns.</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="xs" className="mt-1 text-good" />
            <span>Credentials and configuration are untouched — it reconnects on its own.</span>
          </li>
          <li className="flex items-start gap-2">
            <Icon name="check" size="xs" className="mt-1 text-good" />
            <span>Telemetry already recorded here is unaffected.</span>
          </li>
        </ul>

        <p className="border-t border-line pt-3 text-ink-3">
          The reset is queued, not sent: the device collects it on its next connection. If it is
          offline — in a basement, or parked with the ignition off — the command waits an hour and
          then expires rather than restarting the unit at an unpredictable moment. You can watch
          it on this page.
        </p>
      </div>
    </Modal>
  );
}
